import { removeWmsLayer } from '../../../common/functions/remove_wms_layer.function';
import mapboxgl from 'mapbox-gl';
import { geoServerLinks } from '../../../common/functions/get_geoServer_links.function';

mapboxgl.accessToken = process.env.REACT_APP_PUBLIC_MAP_BOX_GL_ACCESS_TOKEN;

const formatTooltipValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const formatTooltipDate = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTooltipLength = (value) => {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return `${num.toFixed(2)} KM`;
  }
  return '--';
};

const parseLengthValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const numericString = value.replace(/[^0-9.-]/g, '');
    if (!numericString) {
      return undefined;
    }
    const parsed = Number(numericString);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const resolveInventoryLength = (source = {}) => parseLengthValue(source.length);

export const addWmsLayer = async (
  map,
  currentKmzNetwork,
  popupRef,
  stepRef,
  showBackdrop,
  hideBackdrop,
  controller,
  inventoryData
) => {
  showBackdrop();
  try {
    if (map.current.getSource('linksLayers')) {
      removeWmsLayer(map);
    }
    if (!map.current.getSource('linksLayers')) {
      const data = await geoServerLinks(currentKmzNetwork, controller.signal);

      if (!data) {
        console.log('request aborted by user');
        return;
      }

      map.current.addSource('linksLayers', {
        type: 'geojson',
        data: data,
      });

      map.current.addLayer({
        id: 'linksLayers',
        type: 'line',
        source: 'linksLayers',
        paint: {
          'line-color': 'green',
          'line-opacity': 0.4,
          'line-width': 3,
        },
      });

      map.current.addLayer({
        id: 'linksLayers-dashed',
        type: 'line',
        source: 'linksLayers',
        paint: {
          'line-color': 'green',
          'line-opacity': 0.4,
          'line-width': 3,
        },
      });
      const dashArraySequence = [
        [0, 4, 3],
        [0.5, 4, 2.5],
        [1, 4, 2],
        [1.5, 4, 1.5],
        [2, 4, 1],
        [2.5, 4, 0.5],
        [3, 4, 0],
        [0, 0.5, 3, 3.5],
        [0, 1, 3, 3],
        [0, 1.5, 3, 2.5],
        [0, 2, 3, 2],
        [0, 2.5, 3, 1.5],
        [0, 3, 3, 1],
        [0, 3.5, 3, 0.5],
      ];

      const animateDashArray = (timestamp) => {
        const newStep = parseInt((timestamp / 120) % dashArraySequence.length);

        map.current.setPaintProperty(
          'linksLayers-dashed',
          'line-dasharray',
          dashArraySequence[stepRef.current]
        );
        stepRef.current = newStep;

        requestAnimationFrame(animateDashArray);
      };

      // animateDashArray(0);
      if (map?.current.getLayer('linksLayers-dashed')) {
        animateDashArray(0);
      }

      map.current.on('mouseenter', 'linksLayers', () => {
        map.current.getCanvasContainer().style.cursor = 'pointer';
      });
      map.current.on('mouseenter', 'linksLayers', (e) => {
        const feature = map.current.queryRenderedFeatures(e.point, {
          layers: ['linksLayers'],
        })[0];

        if (!feature) {
          return;
        }

        const properties = feature.properties;
        const lng = e.lngLat;

        const matchedItem = inventoryData.find((item) => {
          const inventoryLayer = item.layer_name ?? '';
          const propertyLayer = properties?.layer_name ?? properties?.layerName ?? '';
          return inventoryLayer.trim().toLowerCase() === propertyLayer.trim().toLowerCase();
        });

        const inventoryLength = resolveInventoryLength(matchedItem);
        const mergedData = {
          ...properties,
          ...matchedItem,
        };

        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        const tooltipHtml = `
          <div class="mapTooltip-container">
            <div class="title_container_tooltip">
              <div class="title_text_container_tooltip">
                <h3 class="tooltip-heading">${formatTooltipValue(mergedData.layer_name)}</h3>
                <p class="tooltip-item"><strong>Vendor:</strong> ${formatTooltipValue(mergedData.vendor)}</p>
                <p class="tooltip-item"><strong>Fiber Type:</strong> ${formatTooltipValue(mergedData.fiber_type)}</p>
                <p class="tooltip-item"><strong>Fiber Pair:</strong> ${formatTooltipValue(mergedData.fiber_pair ?? mergedData.pair_count)}</p>
                <p class="tooltip-item"><strong>Used Pair:</strong> ${formatTooltipValue(mergedData.used_pair)}</p>
                <p class="tooltip-item"><strong>Available Pair:</strong> ${formatTooltipValue(mergedData.available_pair ?? mergedData.unused_pair)}</p>
                <p class="tooltip-item"><strong>Link Rate (Gbps):</strong> ${formatTooltipValue(mergedData.link_rate_gbps ?? mergedData.link_rate)}</p>
                <p class="tooltip-item"><strong>Length:</strong> ${formatTooltipLength(inventoryLength)}</p>
                <p class="tooltip-item"><strong>Tenancy Type:</strong> ${formatTooltipValue(mergedData.fiber_tenancy_type ?? mergedData.tenancy_type)}</p>
                <p class="tooltip-item"><strong>Commission Date:</strong> ${formatTooltipDate(mergedData.commission_date ?? mergedData.commissioned_at)}</p>
                <p class="tooltip-item"><strong>Scope Year:</strong> ${formatTooltipValue(mergedData.scope_year ?? mergedData.scopeYear)}</p>
                <p class="tooltip-item"><strong>Region:</strong> ${formatTooltipValue(mergedData.source_region ?? mergedData.region)}</p>
                <p class="tooltip-item"><strong>City:</strong> ${formatTooltipValue(mergedData.source_city ?? mergedData.city)}</p>
                <p class="tooltip-item"><strong>Ring:</strong> ${formatTooltipValue(mergedData.source_ring ?? mergedData.ring_unique_name)}</p>
                <p class="tooltip-item"><strong>Source Site:</strong> ${formatTooltipValue(mergedData.source_site_code)}</p>
                <p class="tooltip-item"><strong>Sink Site:</strong> ${formatTooltipValue(mergedData.sink_site_code)}</p>
                <p class="tooltip-item"><strong>Link Type:</strong> ${formatTooltipValue(mergedData.link_type)}</p>
              </div>
            </div>
          </div>
        `;

        popupRef.current = new mapboxgl.Popup({ closeButton: false })
          .setLngLat(lng)
          .setHTML(tooltipHtml)
          .addTo(map.current);
      });

      map.current.on('mouseleave', 'linksLayers', () => {
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
      });

      map.current.on('mouseleave', 'linksLayers', () => {
        map.current.getCanvasContainer().style.cursor = '';
      });
    }
  } catch (error) {
    console.error('Error adding WFS layer:', error);
  }
  hideBackdrop();
};
